import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../../club-api-client";
import type { UpdateClub } from "../../../types";
import { queryKeys } from "../../query-keys";

export function useClubDetail(clubId: string | null) {
	return useQuery({
		queryKey: queryKeys.clubPortal.detail(clubId ?? ""),
		queryFn: () => clubApi.club.get(clubId!),
		enabled: !!clubId,
	});
}

export function useUpdateClub(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: UpdateClub) => clubApi.club.update(clubId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.detail(clubId),
			}),
	});
}

export function useUploadClubBanner(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (file: File) => clubApi.club.uploadBanner(clubId, file),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.detail(clubId),
			}),
	});
}

export function useDeleteClubOwner(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => clubApi.club.delete(clubId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal"],
			}),
	});
}

export function useUploadClubAvatar(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (file: File) => clubApi.club.uploadAvatar(clubId, file),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.detail(clubId),
			}),
	});
}

// Starts a self-serve subscription trial (club = 30 days, club_pro = 60 days).
// The tier claim updates asynchronously via the backend outbox, so feature
// unlock lags a few seconds; the club detail (trial badge/countdown) refreshes
// immediately. Invalidating the session nudges the claim-derived tier to catch up.
export function useStartTrial(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (tier: "club" | "club_pro") => clubApi.club.startTrial(clubId, tier),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.detail(clubId),
			});
			queryClient.invalidateQueries({ queryKey: ["session"] });
		},
	});
}
